import React, { useEffect } from 'react';
import './LegalPages.css';

function PrivacyPolicy() {
  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="legal-page">
      <div className="legal-header">
        <h1>Privacy Policy</h1>
      </div>
      
      <div className="legal-subheader">
        Last Updated: February 18, 2026
      </div>
      
      <div className="legal-container">
        <section className="legal-section">
          <h2>Purpose of this statement</h2>
          <p>
            This privacy statement (this Statement) explains how and why Think A Move, Ltd (TAM) process personal information (called Personal Data under applicable data protection law and in this Statement) that we collect from or that are provided to us by people and organizations with whom we have contact as part of our business activities, including through the Tytonvoice website, www.Tytonvoice.ai. Tytonvoice is a Think A Move (TAM) technology.
          </p>
        </section>

        <section className="legal-section">
          <h2>Who we are</h2>
          <p>
            Think A Move, Ltd. is located at 4807 Rockside Road, Suite 320, Independence, OH 44131. Think A Move, Ltd. is the controller of and is responsible for your Personal Data. Consistent with our core values of Integrity and Partnership, we are committed to protecting and using fairly any Personal Data that we hold.
          </p>
        </section>

        <section className="legal-section">
          <h2>Terms we use in this Statement</h2>
          <p>
            This section explains three terms that we use in the rest of this Statement.
            <ul>
              <li>
                <strong>Legitimate Interests</strong> means the interests of Think A Move, Ltd. in conducting their business activities. When we process your Personal Data based on our Legitimate Interests, we consider and balance any potential impact on you and your data protection rights. We will not use your Personal Data for activities where our interests are overridden by the impact on you unless we have your consent or are otherwise required or permitted by law. You have certain rights when we process your Personal Data on this basis. For more on your data subject rights please see section 8 of this Statement.
              </li>
              <li>
                <strong>Performance of a Contract</strong> means processing your Personal Data where it is necessary for the performance of a contract to which you or an organization with which you are connected is a party.
              </li>
              <li>
                <strong>Comply with Legal Obligations</strong> means processing your Personal Data where it is necessary for compliance with a legal or regulatory obligation that applies to Think A Move, Ltd.. When do we process your Personal Data?
              </li>
            </ul>
          </p>
        </section>

        <section className="legal-section">
          <h2>We may process your Personal Data</h2>
          <p>
            <ul>
                <li>If you visit our Website. Please see the section ‘How Do We Share Your Personal Data?’ below for more on this.</li>
                <li>If we deal with you, or an organization with which you are connected, in the course of our business activities. Please see section 4.4 of this Statement for more on this.</li>
            </ul>
          </p>
        </section>

        <section className="legal-section">
          <h2>If you visit our Website</h2>
          <p>
            <strong>What Personal Data do we process about visitors to our Website?</strong>
            <br />
            We process Personal Data about visitors to the Tytonvoice website. This may include:
            <ul>
            <li>The name of the domain and host from which you access the internet.</li>
            <li>The internet protocol (IP) address of the computer you are using.</li>
            <li>The browser software you are using.</li>
            <li>The operating system you are using.</li>
            <li>The date and time at which you access our Website.</li>
            <li>The email address you use to set up an account.</li>
            <li>The physical address you enter into your account.</li>
            <li>The phone number you enter into your account.</li>
            </ul>
            Think A Move, Ltd. will also collect and process information about your interaction with the Tytonvoice website using cookies. For more information on how we use cookies, please see the section below regarding Cookies and Web Beacons.
            <br /><br />
            <strong>What is the legal basis for this processing?</strong>
            <br />
            We rely on the following legal grounds for this processing:
            <br /><br />
            <strong>Legitimate Interests, for example:</strong>
            <ul>
            <li>Providing access to our Website and enabling its use.</li>
            <li>Protecting the security and integrity of our Website, IT systems and other systems.</li>
            <li>Providing, improving, monitoring and testing the effectiveness of our Website.</li>
            <li>Ensuring the content on our Website is presented in an effective, accessible and user-friendly format.</li>
            <li>Monitoring metrics such as numbers of visitors, traffic data and demographic patterns.</li>
            </ul>
            <strong>When do we share your Personal Data?</strong>
            <br />
            We may disclose some or all of the Personal Data we collect from you to certain trusted third parties, in accordance with contractual arrangements we have in place with them, including:
            <ul>
            <li>IT service providers.</li>
            <li>Brand owners of products sold on our website.</li>
            </ul>
          </p>
        </section>

        <section className="legal-section">
          <h2>How do we share your Personal Data?</h2>
          <p>
            We may disclose some or all of the Personal Data we collect from you to certain trusted third parties in accordance with contractual arrangements we have in place with them, including:
            <ul>
              <li>
                <strong>Analytics Partners.</strong> We use analytics services such as Google Analytics to collect and process certain analytics data. These services may also collect information about your use of other websites, apps and online resources. You learn about Google’s practices by going to https://www.google.com/policies/privacy/partners
                , and opt-out of them by downloading the Google Analytics opt-out browser add-on, available at https://tools.google.com/dlpage/gaoptout
                .
              </li>
              <li>
                <strong>Aggregated, or De-identified Form.</strong> We may make certain automatically-collected, aggregated, or otherwise de-identified information available to third parties for various purposes, including (i) compliance with various reporting obligations; (ii) for business or marketing purposes; or (iii) to assist such parties in understanding our users’ interests, habits, and usage patterns for certain programs, content, services, advertisements, promotions, and/or functionality available through the website.
              </li>
              <li>
                <strong>As Required By Law and Similar Disclosures.</strong> We may access, preserve, and disclose your information if we believe doing so is required or appropriate to: (i) comply with law enforcement requests and legal process, such as a court order or subpoena or other lawful requests by public authorities, including to meet national security or law enforcement requirements; (ii) respond to your requests; or (iii) protect your, our, or others’ rights, property, or safety. For the avoidance of doubt, the disclosure of your information may occur if you post any objectionable content on or through the Services.
              </li>
              <li>
                <strong>Merger, Sale, or Other Asset Transfers.</strong> We may transfer your information to service providers, advisors, potential transactional partners, or other third parties in connection with the consideration, negotiation, or completion of a corporate transaction in which we are acquired by or merged with another company or we sell, liquidate, or transfer all or a portion of our assets. The use of your information following any of these events will be governed by the provisions of this Privacy Policy in effect at the time the applicable information was collected.
              </li>
              <li>
                <strong>Consent.</strong> We may also disclose your information with your permission or at your direction.
              </li>
            </ul>
          </p>
        </section>

        <section className="legal-section">
          <h2>Personal Data that you provide to us</h2>
          <p>
            It is your responsibility to ensure that all Personal Data you provide to us are complete, accurate, up-to-date, and not misleading.
            <br /><br />
            You may provide Personal Data to us about yourself or about other people. If you provide us with Personal Data about another person, you must ensure that you are entitled to disclose the Personal Data of that other person to us and that we may process that Personal Data in accordance with this Privacy Statement without having to take any further steps. It is your responsibility to ensure that the person concerned is aware of the content of this Privacy Statement.
          </p>
        </section>

        <section className="legal-section">
          <h2>How long do we keep your Personal Data?</h2>
          <p>
            We will keep your Personal Data for the longer of the following two periods:
            <ul>
              <li>Any period required for the Performance of a Contract or to Comply with Legal Obligations.</li>
              <li>For as long as it is relevant to and useful for the purpose for which they were originally collected.</li>
            </ul>     
          </p>
        </section>

        <section className="legal-section">
          <h2>Changes to this Statement</h2>
          <p>
            We may amend this Statement on occasion, in whole or part, at our sole discretion. Any changes will be effective immediately. You should check this page from time-to-time and take note of any changes we make, as they will be binding on you. We will include the effective date on each version of this Statement that we issue.
            <br /><br />
            If at any time we decide to use your Personal Data in a manner significantly different from that described in this Statement, or as otherwise disclosed to you at the time they are collected, we will notify you by e-mail or by telephone, and you will have a choice as to whether or not we use your Personal Data in the new manner.
          </p>
        </section>

        <section className="legal-section">
          <h2>Cookies and web beacons</h2>
          <p>
            We may collect information about your computer, including, where available, your IP address, operating system and browser type, for system administration and to collate aggregate information about how you browse the Tytonvoice website. We use Google Analytics on the Tytonvoice website. Google Analytics uses cookies and web beacons in connection with the services it provides. We also have Facebook pixels in place on our website, which serve a similar function to Google Analytics cookies.
            <br /><br />
            Information about your general Internet use may be obtained by using a cookie file which is stored on the hard drive of your computer. Cookies contain information that is transferred to your computer’s hard drive. Web beacons may also be used. These facilitate the transfer of data from our Website to the Google Analytics website. Cookies and web beacons help us to improve our Website. They may be used to enable us to:
            <ul>
            <li>Estimate usage numbers, patterns and related activity.</li>
            <li>Store information about how you accessed and use our Website and your preferences, and so allow us to customize our Website according to your individual interests.</li>
            <li>Recognize you when you return to our Website.</li>
            </ul>
            Information collected may be transmitted to and stored by Google on servers in the United States. Google may also transfer this information to third parties where required to do so by law, or where such third parties process the information on Google’s behalf. You may refuse to accept cookies by activating the relevant setting on your browser. However, if you select this setting you may be unable to access certain parts of our Website or be unable to avail of some services we provide. Unless you have adjusted your browser setting so that it will refuse cookies, cookies will issue when you log on to our Website.
            <br /><br />
            For more about cookies and our policies regarding cookies, please see our full Cookie Policy.
          </p>
        </section>

        <section className="legal-section">
          <h2>Contact Us</h2>
          <p>
            If you have any questions about this Agreement, please contact us at:
            <br /><br />
            TAM / Think A Move, Ltd.<br />
            4807 Rockside Road, Suite 320<br />
            Independence, OH 44131<br />
            info@think-a-move.com
          </p>
        </section>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
